import * as Dialog from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { TbAlertTriangle, TbTrash, TbX } from "react-icons/tb";
import styled from "styled-components";
import Button from "../../../components/Button";
import { Footnote, Title2 } from "../../../styles/typography";

const StyledOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1101;
  backdrop-filter: blur(8px);
`;

const StyledContent = styled(motion.div)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) !important;
  width: calc(100% - 32px);
  max-width: 600px;
  background: rgb(${(props) => props.theme.colors.white});
  border: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  border-radius: 8px;
  box-sizing: border-box;
  z-index: 1102;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 2px solid rgb(${(props) => props.theme.colors.neutral150});
`;

const DialogTitle = styled(Title2)`
  color: rgb(${(props) => props.theme.colors.neutral600});
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgb(${(props) => props.theme.colors.neutral200});
  svg {
    min-width: 24px;
    min-height: 24px;
  }
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SelectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconCircle = styled.div`
  min-width: 32px;
  min-height: 32px;
  border-radius: 50%;
  background: rgb(${(props) => props.theme.colors.error100});
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgb(${(p) => p.theme.colors.error400});
  svg {
    min-width: 20px;
    min-height: 20px;
    color: rgb(${(props) => props.theme.colors.error400});
  }
`;

const SelectionText = styled(Footnote)`
  font-size: 17px;
  line-height: 19px;
  color: rgb(${(props) => props.theme.colors.neutral400});
  b {
    font-weight: 600;
    color: rgb(${(props) => props.theme.colors.neutral600});
  }
`;

const InfoBox = styled(Footnote)`
  font-size: 15px;
  line-height: 1.5;
  color: rgb(${(props) => props.theme.colors.neutral400});
  background: rgb(${(props) => props.theme.colors.neutral100});
  border-radius: 8px;
  padding: 16px;
`;

const Actions = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  @media screen and (max-width: 480px) {
    justify-content: center;
    flex-direction: column-reverse;
    button {
      width: 100%;
    }
  }
`;

const ActionButton = styled(Button)`
  width: fit-content;
  svg {
    min-height: 24px;
    min-width: 24px;
  }
`;

const DeleteChapterDialog = ({
  open,
  onOpenChange,
  chapterTitle,
  inBook,
  deleting,
  onConfirm,
  onCancel,
}) => {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay asChild>
          <StyledOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
          />
        </Dialog.Overlay>
        <Dialog.Content asChild aria-describedby={undefined}>
          <StyledContent
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15 }}
          >
            <Header>
              <Dialog.Title asChild>
                <DialogTitle as="p">Delete this chapter?</DialogTitle>
              </Dialog.Title>
              <Dialog.Close asChild>
                <CloseButton aria-label="Close" onClick={onCancel}>
                  <TbX />
                </CloseButton>
              </Dialog.Close>
            </Header>

            <Body>
              <SelectionRow>
                <IconCircle>
                  <TbAlertTriangle />
                </IconCircle>
                <SelectionText as="p">
                  <b>"{chapterTitle}"</b> will be permanently deleted.
                </SelectionText>
              </SelectionRow>

              <InfoBox as="p">
                {inBook
                  ? "This chapter is currently included in your book. Deleting it removes it from your book as well, and this can't be undone."
                  : "This can't be undone once deleted."}
              </InfoBox>
            </Body>

            <Actions>
              <ActionButton
                $flat={true}
                variant="tertiary"
                onButtonClick={onCancel}
                disabled={deleting}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant="warning"
                onButtonClick={onConfirm}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : "Delete"} <TbTrash />
              </ActionButton>
            </Actions>
          </StyledContent>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DeleteChapterDialog;
